import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Lock, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Course, CourseLesson } from '@/types/courses';

interface PlayerSidebarTOCProps {
  course: Course;
  selectedLessonId: string | null;
  expandedModules: Set<string>;
  completedLessons: Record<string, boolean | string | null>;
  progress: number;
  onToggleModule: (moduleId: string) => void;
  onSelectLesson: (lesson: CourseLesson) => void;
  onToggleLessonComplete: (e: React.MouseEvent, lessonId: string) => void;
}

export function PlayerSidebarTOC({
  course,
  selectedLessonId,
  expandedModules,
  completedLessons,
  progress,
  onToggleModule,
  onSelectLesson,
  onToggleLessonComplete,
}: PlayerSidebarTOCProps) {
  return (
    <div className="hidden md:flex w-80 flex-shrink-0 flex-col gap-4 bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800 px-4 py-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-bold text-gray-900 dark:text-white text-base truncate">
          {course.title}
        </h2>
        <span className="text-xs text-gray-400 shrink-0">{progress}%</span>
      </div>
      <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#F37022] transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      <ScrollArea className="flex-1 -mr-4 pr-4">
        <div className="space-y-4">
          {course.modules.map((module) => {
            const isExpanded = expandedModules.has(module.id);
            return (
              <div key={module.id}>
                <button
                  onClick={() => onToggleModule(module.id)}
                  className="flex items-center justify-between w-full text-left px-2 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <span className="truncate">{module.title}</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-gray-500 transition-transform duration-200 shrink-0',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>
                {isExpanded && (
                  <div className="space-y-1">
                    {module.lessons.map((lesson) => {
                      const isActive = selectedLessonId === lesson.id;
                      const isDone = !!completedLessons[lesson.id];
                      const isComingSoon = lesson.status === 'Coming Soon';

                      if (isComingSoon) {
                        return (
                          <div
                            key={lesson.id}
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg text-gray-400 dark:text-gray-600 cursor-not-allowed"
                            title="This lesson is coming soon"
                          >
                            <Lock className="w-4 h-4 flex-shrink-0" />
                            <span className="flex-1 min-w-0 line-clamp-2">
                              {lesson.title}
                            </span>
                            <Badge
                              variant="outline"
                              className="ml-auto text-[10px] shrink-0"
                            >
                              Coming Soon
                            </Badge>
                          </div>
                        );
                      }

                      return (
                        <button
                          key={lesson.id}
                          onClick={() => onSelectLesson(lesson)}
                          className={cn(
                            'w-full flex items-start text-left gap-3 px-3 py-2 text-sm rounded-lg transition-colors group relative',
                            isActive
                              ? 'bg-[#F37022]/10 text-[#F37022] font-medium'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                          )}
                        >
                          <div
                            className="mt-0.5 flex-shrink-0 cursor-pointer z-20 hover:scale-110 transition-transform"
                            onClick={(e) => onToggleLessonComplete(e, lesson.id)}
                          >
                            {isDone ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500 fill-green-100 dark:fill-green-900/30" />
                            ) : (
                              <Circle className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
                            )}
                          </div>
                          <span className="flex-1 min-w-0 line-clamp-2">
                            {lesson.title}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
